import { useMemo } from 'react';

import { RootStateOrAny, useSelector } from 'react-redux';

import { getMembership } from '@techstyle/react-accounts';

import { CheckoutFeatures } from '../../constants/checkout';
import { getIsCartFeatureEnabled } from '../../utils/cart';
import { parseDate } from '../../utils/dates';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import {
  getCreditsApplied,
  getMembershipCartItem,
  getOrderRetailDeliveryFee,
  getStoreCredits,
  hasMembershipInCart as hasMembershipInCartSelector,
  isEmpVip as isEMPVipSelector,
  isNMP as isNMPSelector,
} from '../../utils/selectors';
import { useMembership } from '../../utils/useMembership';

const sevenDays = 1000 * 60 * 60 * 24 * 7; // Seven days in milliseconds

function useEMPCredits({ items, tokenQuantity, memberTokenQuantity }) {
  const isEmpVip = useSelector(isEMPVipSelector);
  const tokens = tokenQuantity || memberTokenQuantity || 0;
  const creditsApplied = tokens > 0;
  const empCreditsVipValue = useMemo(
    () =>
      !creditsApplied
        ? 0
        : items
            .filter(item => item.tokenQtyApplied || item.tokensApplied)
            .reduce(
              (total, item) =>
                total + (isEmpVip ? item.vipUnitPrice : item.purchaseUnitPrice),
              0
            ),
    [creditsApplied, isEmpVip, items]
  );

  return [tokens, empCreditsVipValue];
}

function useMemberCredits(order) {
  const isNMP = useSelector(isNMPSelector);
  const { 'token-to-cash': tokenToCashEnabled } = useLDFlags();
  const creditsAppliedQty = useSelector(getCreditsApplied) || 0;
  const [empCreditsUsed, empCreditsVipValue] = useEMPCredits(order);
  const { membershipStoreCredit } = useSelector(getStoreCredits);
  const shouldShowMemberCredits =
    creditsAppliedQty > 0 && isNMP && tokenToCashEnabled;
  if (!shouldShowMemberCredits) {
    return [empCreditsUsed, empCreditsVipValue];
  }
  return [creditsAppliedQty, membershipStoreCredit];
}

function useSubtotal(order) {
  let shouldShowVipSaving = false;
  const { subtotal, nonVipSubtotal, features = [] } = order;
  const membershipItem = useSelector(getMembershipCartItem);
  const { isLead, isPaygo, isDowngradedMember, registrationDate } =
    useSelector(getMembership);
  const isInfluencer = useSelector(
    (state: RootStateOrAny) => state.customer.isInfluencer
  );
  const isRetailPriceTestEnabled = getIsCartFeatureEnabled(
    { features },
    CheckoutFeatures.RETAIL_PRICE_TEST
  );

  const shouldEnableVipSaving =
    (isLead || isPaygo || isDowngradedMember) &&
    !isInfluencer &&
    !isRetailPriceTestEnabled;
  if (shouldEnableVipSaving) {
    const registrationStartDate = parseDate(registrationDate);
    const hasBeenRegisteredForAWeek =
      Date.now() <= registrationStartDate.getTime() + sevenDays;
    shouldShowVipSaving = hasBeenRegisteredForAWeek;
  }
  const resultingSubtotal =
    (shouldShowVipSaving && membershipItem ? nonVipSubtotal : subtotal) || 0;
  return resultingSubtotal;
}

function usePromoSavings(order) {
  const { isVip } = useMembership();
  const hasMembershipInCart = useSelector(hasMembershipInCartSelector);
  const { cartLineDiscounts, subtotalDiscount } = order;
  // All promos with discountId == 11 get grouped inside "NEW MEMBER OFFER" if the user has a membership in the cart.
  // All other promos get grouped in "PROMO SAVINGS".
  // For VIP users, we don't show the new member offer discounts.
  const showNewMemberOffer = hasMembershipInCart && !isVip;
  const newMemberOfferDiscounts = showNewMemberOffer
    ? cartLineDiscounts?.reduce(
        (accum, discount) =>
          discount.discountId === 11 ? accum + discount.amount : accum,
        0
      )
    : null;

  return {
    newMemberOfferDiscounts,
    subTotalDiscountWithoutMemberOffers: !isNaN(newMemberOfferDiscounts)
      ? Math.max(subtotalDiscount - newMemberOfferDiscounts, 0)
      : subtotalDiscount,
  };
}

export function usePricingSummary() {
  const order = useSelector((state: RootStateOrAny) => state.checkout);
  const retailDeliveryFee = useSelector(getOrderRetailDeliveryFee);
  const storeCredits = useSelector(getStoreCredits);
  const { storeCreditsOnly } = storeCredits;
  const {
    cartLineDiscounts,
    discounts: allDiscounts,
    total,
    tax,
    shipping,
    subtotalMinusAllSavings,
    subtotalDiscount,
    tariffSurchargeAmount,
  } = order;
  const discounts = allDiscounts.filter(({ amount }) => amount > 0) || [];

  const { newMemberOfferDiscounts, subTotalDiscountWithoutMemberOffers } =
    usePromoSavings(order);

  const subtotal = useSubtotal(order);
  const [creditsApplied, creditsTotalValue] = useMemberCredits(order);

  const { memberCreditAmount, storeCreditAmount } = order;
  return useMemo(
    () => ({
      total,
      tax,
      subtotalMinusAllSavings,
      subtotal,
      creditsTotalValue,
      creditsApplied,
      shipping,
      memberCreditAmount,
      storeCreditAmount,
      subtotalDiscount,
      discounts,
      subTotalDiscountWithoutMemberOffers,
      newMemberOfferDiscounts,
      cartLineDiscounts,
      tariffSurchargeAmount,
      retailDeliveryFee,
      storeCreditsOnly,
    }),
    [
      total,
      tax,
      subtotalMinusAllSavings,
      subtotal,
      creditsTotalValue,
      creditsApplied,
      shipping,
      memberCreditAmount,
      storeCreditAmount,
      subtotalDiscount,
      discounts,
      subTotalDiscountWithoutMemberOffers,
      newMemberOfferDiscounts,
      cartLineDiscounts,
      tariffSurchargeAmount,
      retailDeliveryFee,
      storeCreditsOnly,
    ]
  );
}
